import { z } from "zod";
import { canonicalizeTag, isRetiredTag } from "./recipe-tags";

// Enums matching Prisma schema
export const difficultyEnum = z.enum(["easy", "medium", "hard"]);
export const mealTypeEnum = z.enum(["breakfast", "lunch", "dinner", "snack"]);
export const costEstimateEnum = z.enum(["budget", "moderate", "expensive"]);
export const recipeStatusEnum = z.enum(["draft", "published"]);

/**
 * Tags are a closed vocabulary (spec §5.3 / D1). Aliases are canonicalized here; unknown values are
 * rejected so a client can never introduce a tag the registry does not know. Derived values (quick,
 * budget, no-cook) are accepted on input and recomputed by finalizeRecipeTags at write time.
 */
export const recipeTagsSchema = z
  .array(z.string().max(50))
  .max(20)
  .transform((tags, ctx) => {
    const canonical: string[] = [];
    const retired: string[] = [];
    const unknown: string[] = [];
    for (const raw of tags) {
      const value = canonicalizeTag(raw);
      if (value === null) {
        if (isRetiredTag(raw)) {
          if (!retired.includes(raw)) retired.push(raw);
        } else if (!unknown.includes(raw)) {
          unknown.push(raw);
        }
      } else if (!canonical.includes(value)) canonical.push(value);
    }
    if (retired.length > 0 || unknown.length > 0) {
      const messages: string[] = [];
      if (retired.length > 0)
        messages.push(`Retired recipe tags: ${retired.join(", ")}`);
      if (unknown.length > 0)
        messages.push(`Unknown recipe tags: ${unknown.join(", ")}`);
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: messages.join("; "),
      });
      return z.NEVER;
    }
    return canonical;
  });

// Ingredient item schema for recipe creation
export const recipeIngredientItemSchema = z
  .object({
    ingredientId: z.string().uuid().optional(), // Optional if creating new ingredient
    ingredientName: z.string().min(1).max(200), // For new or existing ingredients
    amountKind: z
      .enum(["measured", "to_taste", "as_needed", "unspecified"])
      .default("measured"),
    quantity: z.number().positive().nullable(),
    unit: z.string().trim().max(50),
    preparation: z.string().max(100).optional(),
    notes: z.string().max(200).optional(),
    orderIndex: z.number().int().min(0),
  })
  .superRefine((value, ctx) => {
    if (value.amountKind === "measured") {
      if (value.quantity === null)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["quantity"],
          message: "A measured ingredient requires a positive quantity",
        });
      if (!value.unit.trim())
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["unit"],
          message: "A measured ingredient requires a unit",
        });
    } else if (value.quantity !== null || value.unit !== "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["quantity"],
        message:
          "An unquantified ingredient requires null quantity and an empty unit",
      });
    }
  });

// Instruction step schema
export const recipeInstructionSchema = z.object({
  text: z.string().min(1).max(1000),
  imageUrl: z.string().url().optional(),
  orderIndex: z.number().int().min(0),
});

// Nutrition info schema
const nutrientValue = z.number().finite().min(0).nullable().default(null);
export const recipeNutritionSchema = z.object({
  calories: nutrientValue,
  protein: nutrientValue,
  carbs: nutrientValue,
  fat: nutrientValue,
  fiber: nutrientValue,
  sodium: nutrientValue,
  sugar: nutrientValue,
  saturatedFat: nutrientValue,
});

// Recipe creation schema
export const recipeCreateSchema = z.object({
  // Basic info
  title: z
    .string()
    .min(3, "Title must be at least 3 characters")
    .max(200, "Title must be less than 200 characters"),
  description: z.string().max(2000).optional(),
  imageUrl: z.string().url().optional(),

  // Time & Difficulty
  prepTimeMinutes: z.number().int().min(0).max(1440).nullable(), // Max 24 hours
  cookTimeMinutes: z.number().int().min(0).max(1440).nullable(),
  totalTimeMinutes: z.number().int().min(0).max(1440).nullable().optional(),
  difficulty: difficultyEnum.default("easy"),

  // Classification
  cuisine: z.string().max(100).optional(),
  mealTypes: z.array(mealTypeEnum).min(1, "Select at least one meal type"),
  tags: recipeTagsSchema.default([]),

  // Serving & Cost
  servings: z.number().int().min(1).max(20).default(4),
  costEstimate: costEstimateEnum.default("moderate"),

  // Ingredients & Instructions
  ingredients: z
    .array(recipeIngredientItemSchema)
    .min(1, "Recipe must have at least one ingredient"),
  instructions: z
    .array(recipeInstructionSchema)
    .min(1, "Recipe must have at least one instruction step"),

  // Nutrition (optional)
  nutrition: recipeNutritionSchema.optional(),

  // Publishing
  isPublic: z.boolean().default(true),
  status: recipeStatusEnum.optional(), // Will default to 'draft' in database

  // Import metadata (set by the recipe-import flow; harmless if user-supplied)
  sourceUrl: z.string().url().max(2000).optional(),
  sourceName: z.string().max(120).optional(),
  importConfidence: z.number().min(0).max(1).optional(),
});

// Recipe update schema - all fields optional except those needed for identification
export const recipeUpdateSchema = z.object({
  id: z.string().uuid(),

  // Basic info
  title: z.string().min(3).max(200).optional(),
  description: z.string().max(2000).optional().nullable(),
  imageUrl: z.string().url().optional().nullable(),

  // Time & Difficulty
  prepTimeMinutes: z.number().int().min(0).max(1440).nullable().optional(),
  cookTimeMinutes: z.number().int().min(0).max(1440).nullable().optional(),
  totalTimeMinutes: z.number().int().min(0).max(1440).nullable().optional(),
  difficulty: difficultyEnum.optional(),

  // Classification
  cuisine: z.string().max(100).optional().nullable(),
  mealTypes: z.array(mealTypeEnum).optional(),
  tags: recipeTagsSchema.optional(),

  // Serving & Cost
  servings: z.number().int().min(1).max(20).optional(),
  costEstimate: costEstimateEnum.optional(),

  // Ingredients & Instructions
  ingredients: z.array(recipeIngredientItemSchema).optional(),
  instructions: z.array(recipeInstructionSchema).optional(),

  // Nutrition
  nutrition: recipeNutritionSchema.optional().nullable(),

  // Publishing
  isPublic: z.boolean().optional(),
  status: recipeStatusEnum.optional(),
});

// Recipe publish schema - validates recipe is complete before publishing
export const recipePublishSchema = z
  .object({
    id: z.string().uuid(),
  })
  .refine(
    () => true, // Additional validation will happen server-side to check completeness
    { message: "Recipe must be complete before publishing" },
  );

// Type exports
export type RecipeCreateInput = z.infer<typeof recipeCreateSchema>;
export type RecipeUpdateInput = z.infer<typeof recipeUpdateSchema>;
export type RecipePublishInput = z.infer<typeof recipePublishSchema>;
export type RecipeIngredientItem = z.infer<typeof recipeIngredientItemSchema>;
export type RecipeInstruction = z.infer<typeof recipeInstructionSchema>;
export type RecipeNutrition = z.infer<typeof recipeNutritionSchema>;
